import { CONFIG } from '../../config/gameConfig';
import type { Bounds, Drawable } from '../../types';

export type ObstacleKind = 'cactus-small' | 'cactus-large' | 'pterodactyl';

interface Spec {
  width: number;
  height: number;
  baseYOffset: number;
}

const SPECS: Record<ObstacleKind, Spec> = {
  'cactus-small': { width: 17, height: 35, baseYOffset: 0 },
  'cactus-large': { width: 25, height: 50, baseYOffset: 0 },
  pterodactyl: { width: 46, height: 30, baseYOffset: -45 },
};

const COLOR = '#535353';

export class Obstacle implements Drawable {
  x: number;
  y: number;
  readonly width: number;
  readonly height: number;
  private animationFrame = 0;

  constructor(public readonly kind: ObstacleKind, startX: number) {
    const spec = SPECS[kind];
    this.width = spec.width;
    this.height = spec.height;
    this.x = startX;
    this.y = CONFIG.game.groundY - spec.height + spec.baseYOffset;
  }

  update(speed: number): void {
    this.x -= speed;
    this.animationFrame = (this.animationFrame + 1) % 20;
  }

  isOffscreen(): boolean {
    return this.x + this.width < 0;
  }

  bounds(): Bounds {
    return {
      x: this.x + 2,
      y: this.y + 2,
      width: this.width - 4,
      height: this.height - 4,
    };
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = COLOR;
    if (this.kind === 'pterodactyl') {
      this.drawPterodactyl(ctx);
    } else {
      this.drawCactus(ctx);
    }
  }

  private drawCactus(ctx: CanvasRenderingContext2D): void {
    const { x, y, width, height } = this;
    const trunkWidth = Math.max(6, Math.floor(width * 0.4));
    const trunkX = x + Math.floor((width - trunkWidth) / 2);
    ctx.fillRect(trunkX, y, trunkWidth, height);
    const armHeight = Math.floor(height * 0.45);
    const armWidth = Math.max(4, Math.floor(width * 0.3));
    ctx.fillRect(x, y + Math.floor(height * 0.3), armWidth, armHeight);
    ctx.fillRect(x + width - armWidth, y + Math.floor(height * 0.2), armWidth, armHeight);
    ctx.fillRect(x, y + Math.floor(height * 0.3), armWidth, Math.max(4, Math.floor(height * 0.1)));
    ctx.fillRect(
      x + width - armWidth,
      y + Math.floor(height * 0.2),
      armWidth,
      Math.max(4, Math.floor(height * 0.1)),
    );
  }

  private drawPterodactyl(ctx: CanvasRenderingContext2D): void {
    const { x, y, height } = this;
    const wingUp = this.animationFrame < 10;

    ctx.fillRect(x + 8, y + 14, 28, 8);
    ctx.fillRect(x + 30, y + 10, 16, 6);
    ctx.fillRect(x + 40, y + 6, 6, 4);

    if (wingUp) {
      ctx.fillRect(x + 12, y, 20, 14);
    } else {
      ctx.fillRect(x + 12, y + 22, 20, height - 22);
    }
  }
}
